import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { EASE, Eyebrow } from "./Reveal";
import { Wash, Sprig } from "./Watercolor";

const STEPS = [
  {
    n: "01",
    title: "Pick your print",
    desc: "Choose a canopy from the collection, or send us a photo of the flowers you love — we'll sketch your design for free before anyone picks up a brush.",
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_custom-umbrellas/artifacts/26a0470ecccda35a_IMG_2999.jpeg",
  },
  {
    n: "02",
    title: "Choose your handle",
    desc: "Chestnut, bamboo or maple — carved, sanded and finished with natural oil. We'll match the wood to your canopy's mood.",
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_custom-umbrellas/artifacts/9ba007e7e04c98fb_IMG_2998.jpeg",
  },
  {
    n: "03",
    title: "Monogram & dispatch",
    desc: "We hand-letter your monogram, wrap everything in tissue and a gift box, and ship within 14 days of your approval. Rain, you're on.",
    img: "https://customer-assets-7cd3h4nn.emergentagent.net/job_custom-umbrellas/artifacts/1d2a30ada743b469_IMG_2997.jpeg",
  },
];

export default function Commission() {
  const [active, setActive] = useState(0);

  return (
    <section id="process" className="relative py-24 sm:py-28" data-testid="commission-section">
      <Wash color="#FFECD1" size={480} opacity={0.42} className="-top-10 -right-32" />
      <Sprig color="#F472B6" flip className="hidden lg:block bottom-16 left-[3%] w-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>The Commission</Eyebrow>
          <h2 className="mt-4 font-serif font-medium tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
            From sketch to shower in three steps.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
          <div>
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <button
                  data-testid={`commission-step-${i + 1}`}
                  onClick={() => setActive(i)}
                  className="group w-full text-left border-b border-ink/10 py-6 sm:py-7 transition-colors"
                >
                  <div className="flex items-center gap-5 sm:gap-7">
                    <span
                      className={`font-mono text-sm transition-colors ${active === i ? "text-terracotta" : "text-ink/40"}`}
                    >
                      {s.n}
                    </span>
                    <span
                      className={`font-serif text-2xl sm:text-3xl tracking-tight transition-colors ${active === i ? "text-terracotta" : "text-ink group-hover:text-terracotta/70"}`}
                    >
                      {s.title}
                    </span>
                    <span className={`ml-auto h-2.5 w-2.5 rounded-full transition-colors ${active === i ? "bg-terracotta" : "bg-ink/15"}`} />
                  </div>
                  <AnimatePresence initial={false}>
                    {active === i && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden text-ink/70 leading-relaxed pl-0 sm:pl-12"
                      >
                        <span className="block pt-3 pr-2 max-w-md text-sm sm:text-base">{s.desc}</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal className="lg:sticky lg:top-28" delay={0.15}>
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border-4 border-white shadow-2xl shadow-moss/20 bg-peach">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={STEPS[active].img}
                  alt={STEPS[active].title}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="commission-preview-image"
                />
              </AnimatePresence>
              <span className="absolute bottom-4 left-4 rounded-full bg-cream/90 backdrop-blur px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider text-ink/70" data-testid="commission-step-badge">
                Step {STEPS[active].n} — {STEPS[active].title}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
